import React, { useState, useEffect } from 'react';
import { Button, TextField, Grid, MenuItem, Select, InputLabel, FormControl, Snackbar, Alert } from '@mui/material';
import { getDatabase, ref, push, set, get } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import './AddEvent.css';
import Autocomplete from '@mui/material/Autocomplete'; 
import fc from '../firebaseConfig';

const db = getDatabase(fc);

const getRoundedCurrentDateTime = () => {
  const now = new Date();
  if (now.getMinutes() > 0) {
    now.setHours(now.getHours() + 3);
  }
  now.setMinutes(0);
  now.setSeconds(0);
  now.setMilliseconds(0);
  return now;
};

const removeOldestClientEvents = async (clientId, excessEventCount) => {
  const eventsRef = ref(db, 'events');
  const eventsSnapshot = await get(eventsRef);

  if (eventsSnapshot.exists()) {
    const eventsData = eventsSnapshot.val();
    let clientEvents = [];

    // Przejdź przez wydarzenia i dodaj te związane z danym klientem do tablicy
    for (const key in eventsData) {
      if (eventsData[key].clientId === clientId) {
        clientEvents.push({ key, ...eventsData[key] });
      }
    }

    // Posortuj wydarzenia według daty startu (najstarsze jako pierwsze)
    clientEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    // Usuń najstarsze wydarzenia, aż liczba będzie równa 20
    for (let i = 0; i < excessEventCount; i++) {
      const eventToDelete = clientEvents[i];
      const eventRef = ref(db, `events/${eventToDelete.key}`);
      await set(eventRef, null);  // Usuwa wydarzenie
    }
  }
};

const countClientEvents = async (clientId) => {
  const eventsRef = ref(db, 'events');
  const eventsSnapshot = await get(eventsRef);
  
  if (eventsSnapshot.exists()) {
    const eventsData = eventsSnapshot.val();
    let clientEventsCount = 0;

    // Licz wydarzenia dla danego klienta
    for (const key in eventsData) {
      if (eventsData[key].clientId === clientId) {
        clientEventsCount++;
      }
    }

    console.log(`Liczba wydarzeń dla klienta ${clientId}: ${clientEventsCount}`);

    // Jeśli liczba wydarzeń przekracza 20, usuń nadmiarowe wydarzenia
    if (clientEventsCount > 20) {
      const excessEventCount = clientEventsCount - 20;
      console.log(`Usuwanie ${excessEventCount} najstarszych wydarzeń klienta ${clientId}`);
      await removeOldestClientEvents(clientId, excessEventCount);
    }

    return clientEventsCount;
  } else {
    console.log('Brak wydarzeń w bazie.');
    return 0;
  }
};


const addEvent = async (event) => {
  const eventsRef = ref(db, 'events');
  const newEventRef = push(eventsRef);
  await set(newEventRef, event);
};

const AddEvent = () => {
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dayMismatchError, setDayMismatchError] = useState(false); // New state for day mismatch error
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    if (localStorage.getItem('LOGGED_IN') !== 'true') {
      navigate('/');
    }
  }, [navigate]);

  const roundedStartDate = getRoundedCurrentDateTime();
  const roundedEndDate = new Date(roundedStartDate);
  roundedEndDate.setHours(roundedEndDate.getHours() + 1);
  const [startDate, setStartDate] = useState(roundedStartDate.toISOString().slice(0, -8));
  const [endDate, setEndDate] = useState(roundedEndDate.toISOString().slice(0, -8));
  const [title, setTitle] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [price, setPrice] = useState('');

  useEffect(() => {
    const fetchEmployeesAndClients = async () => {
      const employeesRef = ref(db, 'employees');
      const clientsRef = ref(db, 'clients');
  
      const [employeesSnapshot, clientsSnapshot] = await Promise.all([get(employeesRef), get(clientsRef)]);
  
      if (employeesSnapshot.exists()) {
        const employeesData = [];
        employeesSnapshot.forEach((childSnapshot) => {
          employeesData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        setEmployees(employeesData);
      }
  
      if (clientsSnapshot.exists()) {
        const clientsData = [];
        clientsSnapshot.forEach((childSnapshot) => {
          clientsData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
  
        // Sort clients alphabetically by name
        clientsData.sort((a, b) => a.name.localeCompare(b.name));
        
        setClients(clientsData);
      }
    };
  
    fetchEmployeesAndClients();
  }, []);
  

  const handleSave = async () => {
    setErrorMessage('');
    setDayMismatchError(false); // Reset the day mismatch error
    const errors = {};
  
    if (!title) errors.title = true;
    if (!selectedEmployee) errors.selectedEmployee = true;
    if (!selectedClient) errors.selectedClient = true;
    if (!price) errors.price = true;
  
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrorMessage('Należy wypełnić wszystkie pola!');
      return;
    }

    // Validate that the start date is earlier than the end date
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start >= end) {
      setErrorMessage('Data i godzina rozpoczęcia nie może być późniejsza lub równa dacie zakończenia.');
      return;
    }

    // Validate that the start and end dates are on the same day
    if (start.toISOString().slice(0, 10) !== end.toISOString().slice(0, 10)) {
      setDayMismatchError(true);
      return;
    }
  
    const priceValue = parseFloat(price);
    if (isNaN(priceValue)) {
      setErrorMessage('Cena musi być poprawną liczbą.');
      return;
    }
  
    const selectedEmp = employees.find(emp => emp.id === selectedEmployee);
    const selectedClientObj = clients.find(client => client.id === selectedClient);
  
    if (!selectedEmp || !selectedClientObj) {
      setErrorMessage('Wybrano niepoprawnego pracownika lub klienta.');
      return;
    }
  
    const newEvent = {
      title,
      startDate,
      endDate,
      color: selectedEmp.color,
      employeeName: selectedEmp.name,
      employeeNumber: selectedEmp.number,
      name: selectedClientObj.name,
      phone: selectedClientObj.number,
      clientId: selectedClientObj.id,
      employeeId: selectedEmp.id,
      price: priceValue
    };
  
    await addEvent(newEvent);
    setOpenSnackbar(true);
    await countClientEvents(selectedClientObj.id);
    // Reset the form fields
    setTitle('');
    setStartDate(roundedStartDate.toISOString().slice(0, -8));
    setEndDate(roundedEndDate.toISOString().slice(0, -8));
    setSelectedEmployee('');
    setSelectedClient('');
    setPrice('');
  
    // Delay navigation to allow Snackbar to be visible
    setTimeout(() => navigate('/calendar'), 2000);
  };
  

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleInputChange = (setter, field) => (event) => {
    setter(event.target.value);
    setFieldErrors(prevErrors => ({ ...prevErrors, [field]: false }));
  };

  const handleAddNewEmployee = () => {
    navigate('/AddEmployee');
  };

  const handleAddNewClient = () => {
    navigate('/AddClient');
  };

  return (
    <div className="add-event">
      <Navbar />
      <div className="form-wrapper">
        <div className="container" style={{width: '40rem'}}>
          <h1>Dodaj Usługę</h1>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Tytuł usługi"
                variant="outlined"
                value={title}
                onChange={handleInputChange(setTitle, 'title')}
                fullWidth
                error={fieldErrors.title}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data i godzina rozpoczęcia"
                type="datetime-local"
                value={startDate}
                onChange={handleInputChange(setStartDate, 'startDate')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data i godzina zakończenia"
                type="datetime-local"
                value={endDate}
                onChange={handleInputChange(setEndDate, 'endDate')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={fieldErrors.selectedEmployee}>
                <InputLabel id="employee-select-label">Pracownik</InputLabel>
                <Select
                  labelId="employee-select-label"
                  value={selectedEmployee}
                  onChange={handleInputChange(setSelectedEmployee, 'selectedEmployee')}
                  fullWidth
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: employee.color,
                          display: 'inline-block',
                          borderRadius: '4px',
                          marginRight: '8px'
                        }}></div>
                        {employee.name}
                      </div>
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleAddNewEmployee}>
                    + Dodaj Nowego Pracownika
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} style={{marginTop: '-10px'}}>
  <Autocomplete
    options={[...clients, { id: 'add-new', name: '+ DODAJ NOWEGO KLIENTA' }]} // Dodanie opcji "DODAJ NOWEGO KLIENTA"
    getOptionLabel={(option) => option.name || ''} // Wyświetlanie nazwy klienta
    filterOptions={(options, { inputValue }) =>
      options.filter((client) =>
        client.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        client.id === 'add-new' // Umożliwia wyświetlenie "DODAJ NOWEGO KLIENTA" mimo braku dopasowania
      )
    }
    value={clients.find(client => client.id === selectedClient) || null} // Znalezienie wybranego klienta na podstawie ID
    onChange={(event, newValue) => {
      if (newValue && newValue.id === 'add-new') {
        handleAddNewClient(); // Przekierowanie do dodawania nowego klienta
      } else {
        setSelectedClient(newValue ? newValue.id : ''); // Ustawienie ID wybranego klienta
      }
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Klient"
        margin="dense"
        error={fieldErrors.selectedClient}
        helperText={fieldErrors.selectedClient ? 'Klient jest wymagany' : ''}
      />
    )}
  />
</Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cena"
                variant="outlined"
                value={price}
                onChange={handleInputChange(setPrice, 'price')}
                fullWidth
                type="number"
                error={fieldErrors.price}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Zapisz Usługę
              </Button>
            </Grid>
          </Grid>
          {errorMessage && (
            <div style={{ color: 'red', marginTop: '16px' }}>
              {errorMessage}
            </div>
          )}
          {dayMismatchError && (
            <div style={{ color: 'red', marginTop: '16px' }}>
              Dzień rozpoczęcia i zakończenia musi być taki sam.
            </div>
          )}
        </div>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Poprawnie dodano usługę
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddEvent;
